import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PRIMARY_MEMBER_DEFAULT_NAME } from 'utils/household';
import RestrictionSearch from './RestrictionSearch';

/** Same modal handles both adding a new member and editing an existing one
 *  (name, allergies, and email — except the primary "Me" member, who
 *  doesn't need an email since there's no one to send a quiz invite to). */
export default function AddMemberModal({
  open,
  onClose,
  onSave,
  editingMember = null,
  isPrimary = false,
  className = '',
}) {
  const [name, setName] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [restrictionLabels, setRestrictionLabels] = useState({});
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editingMember) {
      setName(editingMember.name === PRIMARY_MEMBER_DEFAULT_NAME ? '' : (editingMember.name || ''));
      setSelectedTags(editingMember.restrictionTags || []);
      setRestrictionLabels(editingMember.restrictionLabels || {});
      setEmail(editingMember.email || '');
    } else {
      setName('');
      setSelectedTags([]);
      setRestrictionLabels({});
      setEmail('');
    }
  }, [open, editingMember]);

  if (!open) return null;

  // The primary member's name is optional — blank means "just call me You", and
  // they get no email field because there's no one to send a quiz invite to.
  const editingPrimary = !!editingMember && isPrimary;
  const showEmailField = !editingPrimary;
  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName && !editingPrimary) return;
    const trimmedEmail = email.trim();
    const data = {
      name: trimmedName || (editingPrimary ? PRIMARY_MEMBER_DEFAULT_NAME : editingMember?.name || ''),
      restrictionTags: selectedTags,
      restrictionLabels,
    };
    if (showEmailField) data.email = trimmedEmail;
    onSave(data);
  };

  return (
    <div className={`sf-modal${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true" aria-label={editingMember ? 'Edit family member' : 'Add family member'}>
      <button type="button" className="sf-modal__backdrop" onClick={onClose} aria-label="Close" />
      <form className="sf-modal__panel sf-member-modal" onSubmit={handleSubmit}>
        <h2 className="sf-modal__title">{editingMember ? 'Edit family member' : 'Add family member'}</h2>
        {showEmailField && (
          <p className="sf-page__meta">
            You can also{' '}
            <Link to="/quiz" onClick={onClose}>
              send them the quiz
            </Link>{' '}
            later for a full nutrition profile.
          </p>
        )}

        <label className="sf-modal__label">
          Name
          <input
            className="sf-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={editingPrimary ? PRIMARY_MEMBER_DEFAULT_NAME : 'e.g. Claire'}
            required={!editingPrimary}
          />
        </label>

        <div className="sf-modal__fieldset">
          <p className="sf-modal__legend">Food restrictions</p>
          <RestrictionSearch
            selected={selectedTags}
            labelMap={restrictionLabels}
            onChange={(ids, labels) => {
              setSelectedTags(ids);
              setRestrictionLabels(labels);
            }}
          />
        </div>

        {showEmailField && (
          <label className="sf-modal__label">
            Email (optional. we'll invite them to take the quiz, so their meals are tailored too)
            <input
              className="sf-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="claire@email.com"
            />
          </label>
        )}

        <div className="sf-modal__actions">
          <button type="button" className="sf-btn-flow sf-btn-flow--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="sf-btn-flow sf-btn-flow--primary" >
            {editingMember ? 'Save' : 'Add'}
          </button>
        </div>
      </form>
    </div>
  );
}
